import { supabase } from '@/lib/supabaseClient';

// Types
export interface CostEntry {
  id: string;
  tenant_id: string;
  operation_type: 'embedding' | 'query' | 'completion';
  model_name: string;
  token_count: number;
  cost_usd: number;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface CostSummary {
  total_cost: number;
  total_tokens: number;
  by_operation: {
    embedding: { cost: number; tokens: number };
    query: { cost: number; tokens: number };
    completion: { cost: number; tokens: number };
  };
  daily_costs: Array<{
    date: string;
    cost: number;
    tokens: number;
  }>;
}

export interface BudgetAlert {
  threshold: number;
  current_usage: number;
  percentage_used: number;
  alert_triggered: boolean;
}

class CostTrackingService {
  // OpenAI pricing (as of 2024)
  private readonly PRICING = {
    embedding: {
      'text-embedding-ada-002': 0.0001, // per 1K tokens
      'text-embedding-3-small': 0.00002,
      'text-embedding-3-large': 0.00013
    },
    completion: {
      'gpt-3.5-turbo': { input: 0.0005, output: 0.0015 }, // per 1K tokens
      'gpt-4': { input: 0.03, output: 0.06 },
      'gpt-4-turbo': { input: 0.01, output: 0.03 }
    }
  };

  /**
   * Track an AI operation
   */
  async trackOperation(
    tenantId: string,
    operationType: 'embedding' | 'query' | 'completion',
    modelName: string,
    tokenCount: number,
    metadata?: Record<string, any>
  ): Promise<void> {
    try {
      const cost = this.calculateCost(operationType, modelName, tokenCount);

      await supabase
        .from('ai_cost_tracking')
        .insert({
          tenant_id: tenantId,
          operation_type: operationType,
          model_name: modelName,
          token_count: tokenCount,
          cost_usd: cost,
          metadata: {
            ...metadata,
            tracked_at: new Date().toISOString()
          }
        });
    } catch (error) {
      console.error('Error tracking operation cost:', error);
    }
  }

  /**
   * Calculate cost based on model and token count
   */
  calculateCost(
    operationType: 'embedding' | 'query' | 'completion',
    modelName: string,
    tokenCount: number
  ): number {
    let costPerThousand = 0;

    if (operationType === 'embedding') {
      costPerThousand = this.PRICING.embedding[modelName as keyof typeof this.PRICING.embedding] || 0.0001;
    } else {
      // For completions, use average of input/output cost
      const modelPricing = this.PRICING.completion[modelName as keyof typeof this.PRICING.completion];
      if (modelPricing) {
        costPerThousand = (modelPricing.input + modelPricing.output) / 2;
      } else {
        // Default to GPT-3.5 pricing if model not found
        costPerThousand = 0.001;
      }
    }

    return (tokenCount / 1000) * costPerThousand;
  }

  /**
   * Get usage statistics for a tenant
   */
  async getTenantUsage(
    tenantId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<CostSummary> {
    try {
      let query = supabase
        .from('ai_cost_tracking')
        .select('*')
        .eq('tenant_id', tenantId);

      if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
      }
      if (endDate) {
        query = query.lte('created_at', endDate.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching usage:', error);
        return this.getEmptySummary();
      }

      return this.summarizeCosts(data || []);
    } catch (error) {
      console.error('Error getting tenant usage:', error);
      return this.getEmptySummary();
    }
  }

  /**
   * Get monthly costs using the database function
   */
  async getMonthlyUsage(tenantId: string, month?: Date): Promise<any> {
    const targetMonth = month || new Date();
    const monthStr = `${targetMonth.getFullYear()}-${String(targetMonth.getMonth() + 1).padStart(2, '0')}-01`;

    const { data, error } = await supabase.rpc('calculate_monthly_costs', {
      p_tenant_id: tenantId,
      p_month: monthStr
    });

    if (error) {
      console.error('Error fetching monthly costs:', error);
      return [];
    }

    return data;
  }

  /**
   * AIC-3.1: assistant usage for the month broken down by routing tier
   * (haiku vs sonnet). Reads the new ai_cost_tracking.tier column.
   */
  async getUsageByTier(
    tenantId: string,
    month?: Date
  ): Promise<{ tier: string; cost: number; tokens: number; turns: number }[]> {
    const target = month || new Date();
    const start = new Date(target.getFullYear(), target.getMonth(), 1).toISOString();

    const { data, error } = await supabase
      .from('ai_cost_tracking')
      .select('tier, cost_usd, token_count')
      .eq('tenant_id', tenantId)
      .not('tier', 'is', null)
      .gte('created_at', start);

    if (error) {
      console.error('Error fetching usage by tier:', error);
      return [];
    }

    const byTier = new Map<string, { cost: number; tokens: number; turns: number }>();
    (data || []).forEach((r: any) => {
      const t = r.tier || 'unknown';
      const e = byTier.get(t) || { cost: 0, tokens: 0, turns: 0 };
      e.cost += Number(r.cost_usd) || 0;
      e.tokens += Number(r.token_count) || 0;
      e.turns += 1;
      byTier.set(t, e);
    });

    return Array.from(byTier.entries())
      .map(([tier, v]) => ({ tier, ...v }))
      .sort((a, b) => b.cost - a.cost);
  }

  /**
   * Summarize cost data
   */
  private summarizeCosts(entries: CostEntry[]): CostSummary {
    const summary: CostSummary = {
      total_cost: 0,
      total_tokens: 0,
      by_operation: {
        embedding: { cost: 0, tokens: 0 },
        query: { cost: 0, tokens: 0 },
        completion: { cost: 0, tokens: 0 }
      },
      daily_costs: []
    };

    // Group by operation type
    entries.forEach(entry => {
      summary.total_cost += entry.cost_usd;
      summary.total_tokens += entry.token_count;

      const opType = entry.operation_type;
      if (opType in summary.by_operation) {
        summary.by_operation[opType].cost += entry.cost_usd;
        summary.by_operation[opType].tokens += entry.token_count;
      }
    });

    // Group by day
    const dailyData = new Map<string, { cost: number; tokens: number }>();
    entries.forEach(entry => {
      const date = entry.created_at.split('T')[0];
      const existing = dailyData.get(date) || { cost: 0, tokens: 0 };
      existing.cost += entry.cost_usd;
      existing.tokens += entry.token_count;
      dailyData.set(date, existing);
    });

    // Convert to array and sort
    summary.daily_costs = Array.from(dailyData.entries())
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return summary;
  }

  /**
   * Get empty summary object
   */
  private getEmptySummary(): CostSummary {
    return {
      total_cost: 0,
      total_tokens: 0,
      by_operation: {
        embedding: { cost: 0, tokens: 0 },
        query: { cost: 0, tokens: 0 },
        completion: { cost: 0, tokens: 0 }
      },
      daily_costs: []
    };
  }

  /**
   * Set budget alert for a tenant
   */
  async setBudgetAlert(
    tenantId: string,
    monthlyBudget: number
  ): Promise<void> {
    // Store budget in tenant metadata or separate table
    // This is a placeholder - you'd need to create a budget_alerts table
    const metadata = {
      monthly_budget: monthlyBudget,
      alert_threshold: monthlyBudget * 0.8, // Alert at 80%
      updated_at: new Date().toISOString()
    };

    // For now, we'll store in localStorage as a simple solution
    localStorage.setItem(`budget_${tenantId}`, JSON.stringify(metadata));
  }

  /**
   * Check budget status
   */
  async checkBudgetStatus(tenantId: string): Promise<BudgetAlert | null> {
    // Get budget from localStorage (in production, use database)
    const budgetStr = localStorage.getItem(`budget_${tenantId}`);
    if (!budgetStr) return null;

    const budget = JSON.parse(budgetStr);
    
    // Get current month usage
    const usage = await this.getMonthlyUsage(tenantId);
    const totalCost = usage.reduce((sum: number, item: any) => sum + (item.total_cost || 0), 0);

    const percentageUsed = (totalCost / budget.monthly_budget) * 100;

    return {
      threshold: budget.monthly_budget,
      current_usage: totalCost,
      percentage_used: percentageUsed,
      alert_triggered: totalCost >= budget.alert_threshold
    };
  }

  /**
   * Generate usage report
   */
  async generateReport(
    tenantId: string,
    startDate: Date,
    endDate: Date
  ): Promise<string> {
    const usage = await this.getTenantUsage(tenantId, startDate, endDate);
    
    const report = `
AI Usage Report
===============
Period: ${startDate.toDateString()} - ${endDate.toDateString()}

Summary:
--------
Total Cost: $${usage.total_cost.toFixed(4)}
Total Tokens: ${usage.total_tokens.toLocaleString()}

By Operation Type:
-----------------
Embeddings: $${usage.by_operation.embedding.cost.toFixed(4)} (${usage.by_operation.embedding.tokens.toLocaleString()} tokens)
Queries: $${usage.by_operation.query.cost.toFixed(4)} (${usage.by_operation.query.tokens.toLocaleString()} tokens)
Completions: $${usage.by_operation.completion.cost.toFixed(4)} (${usage.by_operation.completion.tokens.toLocaleString()} tokens)

Daily Breakdown:
---------------
${usage.daily_costs.map(day => 
  `${day.date}: $${day.cost.toFixed(4)} (${day.tokens.toLocaleString()} tokens)`
).join('\n')}
`;

    return report;
  }

  /**
   * Export usage data as CSV
   */
  async exportToCSV(tenantId: string, startDate?: Date, endDate?: Date): Promise<string> {
    let query = supabase
      .from('ai_cost_tracking')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });

    if (startDate) {
      query = query.gte('created_at', startDate.toISOString());
    }
    if (endDate) {
      query = query.lte('created_at', endDate.toISOString());
    }

    const { data, error } = await query;

    if (error || !data) {
      console.error('Error exporting data:', error);
      return '';
    }

    // Create CSV
    const headers = ['Date', 'Operation', 'Model', 'Tokens', 'Cost (USD)'];
    const rows = data.map(entry => [
      entry.created_at,
      entry.operation_type,
      entry.model_name,
      entry.token_count,
      entry.cost_usd.toFixed(6)
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    return csv;
  }
}

// Export singleton instance
export const costTrackingService = new CostTrackingService();